import { useEffect, useRef, useState } from 'react';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import { getApiBaseUrl, getPublicAssetUrl } from '../../utils/api.js';
import './AboutUs.css';

const defaultGratitudeDocuments = [
  {
    file: 'Благодарственное_письмо_АО_ТАРКЕТТ.pdf',
    preview: 'vdpo-gratitude-preview-tarkett.jpg',
    title: 'Благодарственное письмо АО ТАРКЕТТ',
  },
  {
    file: 'photo_2026-05-31_00-56-16.jpg',
    preview: 'photo_2026-05-31_00-56-16.jpg',
    title: 'Благодарность школы N 29',
  },
  {
    file: 'photo_2026-05-31_00-56-12.jpg',
    preview: 'photo_2026-05-31_00-56-12.jpg',
    title: 'Благодарность СамЛИТ',
  },
  {
    file: 'photo_2026-05-31_00-56-07.jpg',
    preview: 'photo_2026-05-31_00-56-07.jpg',
    title: 'Благодарность школы N 12',
  },
  {
    file: 'photo_2026-05-31_00-56-02.jpg',
    preview: 'photo_2026-05-31_00-56-02.jpg',
    title: 'Благодарность Самарского спортивного лицея',
  },
  {
    file: 'vdpo-gratitude-letter-01.pdf',
    preview: 'vdpo-gratitude-preview-01.jpg',
    title: 'Благодарность ВДПО 1',
  },
  {
    file: 'vdpo-gratitude-letter-02.pdf',
    preview: 'vdpo-gratitude-preview-02.jpg',
    title: 'Благодарность ВДПО 2',
  },
  {
    file: 'vdpo-gratitude-letter-03.pdf',
    preview: 'vdpo-gratitude-preview-03.jpg',
    title: 'Благодарность ВДПО 3',
  },
  {
    file: 'vdpo-gratitude-letter-04.pdf',
    preview: 'vdpo-gratitude-preview-04.jpg',
    title: 'Благодарность ВДПО 4',
  },
  {
    file: 'vdpo-gratitude-letter-05.pdf',
    preview: 'vdpo-gratitude-preview-05.jpg',
    title: 'Благодарность ВДПО 5',
  },
  {
    file: 'vdpo-gratitude-letter-06.pdf',
    preview: 'vdpo-gratitude-preview-06.jpg',
    title: 'Благодарность ВДПО 6',
  },
  {
    file: 'vdpo-gratitude-letter-07.pdf',
    preview: 'vdpo-gratitude-preview-07.jpg',
    title: 'Благодарность ВДПО 7',
  },
];

function getGratitudePath(documentName) {
  return `/images/vdpo_gratitude/${documentName}`;
}

function getGratitudePreviewPath(documentName) {
  return `/images/vdpo_gratitude/${documentName}`;
}

function isPdfDocument(documentName) {
  return documentName.toLowerCase().endsWith('.pdf');
}

function getGratitudeAssetUrl(assetPath = '') {
  return getPublicAssetUrl(assetPath);
}

function getWrappedGratitudeIndex(index, documentsCount) {
  return (index + documentsCount) % documentsCount;
}

const gratitudeSwipeMinDistance = 40;

function AboutUs() {
  const [uploadedGratitudes, setUploadedGratitudes] = useState([]);
  const [activeGratitudeIndex, setActiveGratitudeIndex] = useState(0);
  const gratitudeTouchStartRef = useRef({ x: 0, y: 0 });
  const gratitudeDocuments = [...uploadedGratitudes, ...defaultGratitudeDocuments];
  const activeGratitude = gratitudeDocuments[activeGratitudeIndex];
  const activeGratitudePath = activeGratitude.isUploaded
    ? getGratitudeAssetUrl(activeGratitude.file)
    : getGratitudePath(activeGratitude.file);
  const thumbnailRadius = Math.ceil(gratitudeDocuments.length / 2);
  const gratitudeThumbnailItems = Array.from(
    { length: thumbnailRadius * 2 + 1 },
    (_, itemIndex) => {
      const offset = itemIndex - thumbnailRadius;
      const documentIndex = getWrappedGratitudeIndex(
        activeGratitudeIndex + offset,
        gratitudeDocuments.length,
      );

      return {
        documentIndex,
        documentItem: gratitudeDocuments[documentIndex],
        isActive: offset === 0,
        offset,
      };
    },
  );
  const activeGratitudeLinkText = isPdfDocument(activeGratitude.file)
    ? 'Открыть PDF'
    : 'Открыть изображение';

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/gratitudes`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Could not load gratitudes');
        }

        return response.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setUploadedGratitudes(data.map((item) => ({ ...item, isUploaded: true })));
        }
      })
      .catch(() => {
        setUploadedGratitudes([]);
      });
  }, []);

  function showPreviousGratitude() {
    setActiveGratitudeIndex((currentIndex) => (
      getWrappedGratitudeIndex(currentIndex - 1, gratitudeDocuments.length)
    ));
  }

  function showNextGratitude() {
    setActiveGratitudeIndex((currentIndex) => (
      getWrappedGratitudeIndex(currentIndex + 1, gratitudeDocuments.length)
    ));
  }

  function handleGratitudeTouchStart(evt) {
    const touch = evt.changedTouches[0];

    gratitudeTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }

  function handleGratitudeTouchEnd(evt) {
    const touch = evt.changedTouches[0];
    const swipeDistanceX = touch.clientX - gratitudeTouchStartRef.current.x;
    const swipeDistanceY = touch.clientY - gratitudeTouchStartRef.current.y;

    if (
      Math.abs(swipeDistanceX) < gratitudeSwipeMinDistance
      || Math.abs(swipeDistanceX) <= Math.abs(swipeDistanceY)
    ) {
      return;
    }

    if (swipeDistanceX > 0) {
      showPreviousGratitude();
      return;
    }

    showNextGratitude();
  }

  return (
    <>
      <Header />
      <main className="content">
        <section className="aboutUs">
          <div className="aboutUs__hero">
            <h2 className="aboutUs__title">О нас</h2>
            <p className="aboutUs__lead">
              Самарское областное отделение ВДПО объединяет многолетний опыт,
              практическую работу в сфере пожарной безопасности и
              социально-ориентированные инициативы для жителей региона.
            </p>
          </div>

          <div className="aboutUs__container">
            <div className="aboutUs__img-container">
              <img
                src="/images/samara-vdpo-about.png"
                className="aboutUs__img"
                alt="Фото председателя Самарского обдластного отделения ВДПО"
              />

              <a
                href="https://vdpo.ru/main/o-nas/ustav#Общие%20положения"
                className="aboutUs__button-charter"
                target="_blanck"
              >
                Устав
              </a>
            </div>
            <div className="aboutUs__text-container">
              <h3 className="aboutUs__section-title">Самарское областное отделение ВДПО</h3>
              <p className="aboutUs__text">
                Идея создания общероссийского центра добровольной пожарной
                охраны возникла во время работы Всероссийской пожарной выставки,
                устроенной Русским техническим обществом в 1892 г. в г.
                Санкт-Петербурге, где одновременно с выставкой проходил первый
                съезд русских деятелей пожарной охраны.
              </p>
              <p className="aboutUs__text">
                Общероссийская общественная организация «Всероссийское
                добровольное пожарное общество» – крупнейшая и старейшая в
                России некоммерческая организация в сфере пожарной безопасности.
                ВДПО является крупнейшим производителем и поставщиком широкой
                номенклатуры пожарно- технической продукции и услуг на всей
                территории РФ. числе и в Самарской области.
              </p>
              <p className="aboutUs__text">
                ВДПО сегодня — это 83 региональных отделения в субъектах
                Российской Федерации. В том числе и в Самарской области.{' '}
              </p>
              <p className="aboutUs__text">
                Всероссийское добровольное пожарное общество в Самарской области
                существует с 2006 года. На протяжении всей своей деятельности
                общество с честью выполняет свою главную задачу - обеспечение
                пожарной безопасности предприятий и населения. Стремясь идти в
                ногу со временем, ВДПО предлагает надежное и удобное в
                эксплуатации противопожарное оборудование, прошедшее строгий
                контроль качества, оказывает противопожарные услуги.
              </p>
              <p className="aboutUs__text">
                Большая организационно-массовая и агитационная работа ведется
                специалистами общества среди населения разных возрастных групп,
                что способствует снижению количества пожаров в регионе.
              </p>
              <p className="aboutUs__text">
                На данный момент ВДПО Самарской области объединяет 3 местных
                отделения, кадровый состав насчитывает более 60 человек,
                основная часть которых имеют большой опыт работы в пожарной
                охране. Самарское областное отделение Всероссийского
                добровольного пожарного общества предоставляет полный комплекс
                услуг по разработке, внедрению и сопровождению проектов
                строительства различного функционального назначения и размера,
                выступает соисполнителем федеральных, региональных и местных
                целевых программ, проектов области пожарной безопасности.
                Общество успешно работает по обеспечению пожарной безопасности
                на территории Самарской области уже более 30 лет, является
                членом саморегулируемых организаций в области
                архитектурно-строительного проектирования, строительства и
                изысканий.
              </p>
            </div>
          </div>
        </section>
        <section className="aboutUs__gratitude">
          <div className="aboutUs__gratitude-header">
            <h3 className="aboutUs__section-title">Благодарности и награды</h3>
            <p className="aboutUs__gratitude-lead">
              Подборка благодарственных писем и наград, отражающих участие
              отделения ВДПО в общественной и профессиональной деятельности.
            </p>
          </div>
          <div className="aboutUs__gratitude-carousel">
            <div
              className="aboutUs__gratitude-viewer"
              onTouchStart={handleGratitudeTouchStart}
              onTouchEnd={handleGratitudeTouchEnd}
            >
              <button
                type="button"
                className="aboutUs__gratitude-arrow aboutUs__gratitude-arrow_prev"
                onClick={showPreviousGratitude}
                aria-label="Предыдущая благодарность"
              >
                &#8592;
              </button>
              <img
                src={activeGratitude.isUploaded
                  ? getGratitudeAssetUrl(activeGratitude.preview)
                  : getGratitudePreviewPath(activeGratitude.preview)}
                alt={activeGratitude.title}
                className="aboutUs__gratitude-image"
              />
              <button
                type="button"
                className="aboutUs__gratitude-arrow aboutUs__gratitude-arrow_next"
                onClick={showNextGratitude}
                aria-label="Следующая благодарность"
              >
                &#8594;
              </button>
            </div>

            <div className="aboutUs__gratitude-toolbar">
              <div>
                <p className="aboutUs__gratitude-card-number">
                  Документ {activeGratitudeIndex + 1} из {gratitudeDocuments.length}
                </p>
                <h4 className="aboutUs__gratitude-card-title">
                  {activeGratitude.title}
                </h4>
              </div>
              <a
                href={activeGratitudePath}
                className="aboutUs__gratitude-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {activeGratitudeLinkText}
              </a>
            </div>

            <div className="aboutUs__gratitude-thumbnails-wrap">
              <button
                type="button"
                className="aboutUs__gratitude-thumbnails-arrow"
                onClick={showPreviousGratitude}
                aria-label="Показать предыдущую благодарность"
              >
                &#8592;
              </button>

              <div
                className="aboutUs__gratitude-thumbnails"
                aria-label="Предпросмотр благодарностей"
                onTouchStart={handleGratitudeTouchStart}
                onTouchEnd={handleGratitudeTouchEnd}
              >
                {gratitudeThumbnailItems.map(({
                  documentIndex,
                  documentItem,
                  isActive,
                  offset,
                }) => (
                  <button
                    type="button"
                    className={`aboutUs__gratitude-thumb${isActive ? ' aboutUs__gratitude-thumb_active' : ''}`}
                    key={`${documentItem.file}-${offset}`}
                    onClick={() => setActiveGratitudeIndex(documentIndex)}
                    aria-label={`Показать ${documentItem.title}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <img
                      src={documentItem.isUploaded
                        ? getGratitudeAssetUrl(documentItem.preview)
                        : getGratitudePreviewPath(documentItem.preview)}
                      alt=""
                      className="aboutUs__gratitude-thumb-img"
                    />
                    <span className="aboutUs__gratitude-thumb-title">
                      {documentItem.title}
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="aboutUs__gratitude-thumbnails-arrow"
                onClick={showNextGratitude}
                aria-label="Показать следующую благодарность"
              >
                &#8594;
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default AboutUs;
